import { useReducer } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { StepUpload } from "./steps/step-upload";
import { StepMapColumns } from "./steps/step-map-columns";
import { StepValidate } from "./steps/step-validate";
import { StepConfirm } from "./steps/step-confirm";
import {
  initialState,
  wizardReducer,
  type ColumnMappingOverride,
} from "./state";
import type { CsvValidationResponse, ImportEntityType } from "@adva/types";

interface ImportWizardSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ImportWizardSheet({
  open,
  onOpenChange,
}: ImportWizardSheetProps) {
  const [state, dispatch] = useReducer(wizardReducer, initialState);

  function handleClose() {
    onOpenChange(false);
    // Reset state on next open via the SheetContent unmount cycle.
    dispatch({ type: "RESET" });
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      handleClose();
      return;
    }
    onOpenChange(next);
  }

  const unmappedHeaders =
    state.validation?.column_mapping.unmapped ??
    state.validation?.column_mapping.unmatched_headers ??
    [];
  const effectiveUnmappedCount = unmappedHeaders.filter(
    (h) => !(h in state.mappingOverrides),
  ).length;

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        className="sm:max-w-3xl w-full overflow-y-auto"
        aria-describedby="import-wizard-description"
      >
        <SheetHeader>
          <SheetTitle>New import</SheetTitle>
          <SheetDescription id="import-wizard-description">
            Upload a CSV, map columns to Adva fields, and submit to the staged
            import pipeline.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {state.error && (
            <div
              role="alert"
              className="rounded-md border border-[color:var(--color-danger)]/40 bg-[color:var(--color-danger)]/5 px-3 py-2 text-sm text-[color:var(--color-danger)]"
            >
              {state.error}
            </div>
          )}

          {state.step === 1 && (
            <StepUpload
              entityType={state.entityType}
              file={state.file}
              onSelectEntity={(entityType: ImportEntityType) =>
                dispatch({ type: "SELECT_ENTITY", entityType })
              }
              onSelectFile={(file: File) =>
                dispatch({ type: "SELECT_FILE", file })
              }
              onClearFile={() => dispatch({ type: "CLEAR_FILE" })}
              onValidated={(
                validation: CsvValidationResponse,
                totalRows: number,
              ) => dispatch({ type: "SET_VALIDATION", validation, totalRows })}
              onError={(error: string) =>
                dispatch({ type: "SET_ERROR", error })
              }
            />
          )}

          {state.step === 2 && state.entityType && state.validation && (
            <StepMapColumns
              entityType={state.entityType}
              file={state.file}
              validation={state.validation}
              overrides={state.mappingOverrides}
              onSetMapping={(override: ColumnMappingOverride) =>
                dispatch({ type: "SET_MAPPING", override })
              }
              onClearMapping={(source_header: string) =>
                dispatch({ type: "CLEAR_MAPPING", source_header })
              }
              onSetValidation={(validation: CsvValidationResponse) =>
                dispatch({ type: "SET_VALIDATION", validation })
              }
              onBack={() => dispatch({ type: "GO_TO_STEP", step: 1 })}
              onContinue={() => dispatch({ type: "GO_TO_STEP", step: 3 })}
              onError={(error: string) =>
                dispatch({ type: "SET_ERROR", error })
              }
            />
          )}

          {state.step === 3 && state.validation && (
            <StepValidate
              validation={state.validation}
              unmappedCount={effectiveUnmappedCount}
              onBack={() => dispatch({ type: "GO_TO_STEP", step: 2 })}
              onContinue={() => dispatch({ type: "GO_TO_STEP", step: 4 })}
            />
          )}

          {state.step === 4 &&
            state.entityType &&
            state.file &&
            state.validation && (
              <StepConfirm
                entityType={state.entityType}
                file={state.file}
                validation={state.validation}
                totalRows={state.totalRows}
                onBack={() => dispatch({ type: "GO_TO_STEP", step: 3 })}
                onSubmitted={handleClose}
                onError={(error: string) =>
                  dispatch({ type: "SET_ERROR", error })
                }
              />
            )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
